import { UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '~/hooks/use-search-params';
import { ContentPlaceholder } from '~/modules/common/content-placeholder';
import type { RowsChangeData } from '~/modules/common/data-grid';
import { DataTable, rowKeyById } from '~/modules/common/data-table/data-table';
import { useSortColumns } from '~/modules/common/data-table/sort-columns';
import { useInfiniteRows } from '~/modules/common/data-table/use-infinite-rows';
import { useRowSelection } from '~/modules/common/data-table/use-row-selection';
import { SearchScope } from '~/modules/common/match-highlight/match-scope';
import { channelOrganizationId } from '~/modules/entities/channel-organization-id';
import type { EnrichedChannel } from '~/modules/entities/types';
import { useCan } from '~/modules/entities/use-can';
import { MembersTableBar } from '~/modules/memberships/members-table/members-bar';
import { useColumns } from '~/modules/memberships/members-table/members-columns';
import { membersListQueryOptions } from '~/modules/memberships/query';
import { useMemberUpdateMutation } from '~/modules/memberships/query-mutations';
import { membersSearchDefaults } from '~/modules/memberships/search-params-schemas';
import type { Member, MembersRouteSearchParams } from '~/modules/memberships/types';

export interface MembersTableProps {
  channel: EnrichedChannel;
  isSheet?: boolean;
}

export function MembersTable({ channel, isSheet = false }: MembersTableProps) {
  const { t } = useTranslation();
  const { search, setSearch } = useSearchParams<MembersRouteSearchParams>({ saveDataInSearch: !isSheet, defaults: membersSearchDefaults });

  const { mutate: updateMembership } = useMemberUpdateMutation();

  const { id: entityId, entityType, tenantId } = channel;
  const organizationId = channelOrganizationId(channel);

  const can = useCan();
  const canUpdate = can('update', channel.entityType, channel);

  const { q, role, sort, order } = search;

  const [columns, setColumns] = useColumns(canUpdate, isSheet, entityType);
  const { sortColumns, setSortColumns: onSortColumnsChange } = useSortColumns(sort, order, setSearch);

  // `counts` and `mfa` feed the insight and MFA columns
  const queryOptions = membersListQueryOptions({ entityId, entityType, tenantId, organizationId, ...search, include: 'counts,mfa' });

  const list = useInfiniteRows(queryOptions);
  const { selected, selectedRowIds, onSelectedRowsChange, clearSelection } = useRowSelection(list.rows);

  const onRowsChange = (changedRows: Member[], { indexes, column }: RowsChangeData<Member>) => {
    if (column.key !== 'role') return;

    for (const index of indexes) {
      const { membership } = changedRows[index];
      updateMembership({
        path: { id: membership.id, tenantId, organizationId },
        body: { role: membership.role },
        channelId: entityId,
        channelType: entityType,
      });
    }
  };

  return (
    <>
      <MembersTableBar
        channel={channel}
        selected={selected}
        searchVars={{ q, role, sort, order }}
        setSearch={setSearch}
        queryKey={queryOptions.queryKey}
        columns={columns}
        setColumns={setColumns}
        clearSelection={clearSelection}
        isSheet={isSheet}
        canUpdate={canUpdate}
      />
      <SearchScope q={q}>
        <DataTable<Member>
          {...{
            ...list,
            onRowsChange,
            rowKeyGetter: rowKeyById,
            columns,
            enableVirtualization: true,
            isFiltered: role !== undefined || !!q,
            onResetFilters: () => setSearch({ q: '', role: undefined }),
            selectedRows: selectedRowIds,
            onSelectedRowsChange,
            sortColumns,
            onSortColumnsChange,
            NoRowsComponent: <ContentPlaceholder icon={UsersIcon} title="c:no_resource_yet" titleOptions={{ resource: t('c:member_other') }} />,
          }}
        />
      </SearchScope>
    </>
  );
}
